import { Divider, Stack, Typography, useTheme } from "@mui/material";
import { connect } from "react-redux";
import { AttributeRenderer, useAttributeGridForm } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { bulkConfigureProductsActions } from "../../bulk-configure-products-store";
import { TProductTypeLine } from "../../bulk-configure-products-utils";

const ownActions = {
  updateAttributeRequest: bulkConfigureProductsActions.updateAttributeRequest
};

type Props = typeof ownActions & {
  line: TProductTypeLine;
};

const BulkAttributeSectionBase = (props: Props) => {
  const { line, updateAttributeRequest } = props;

  const theme = useTheme();
  const configuratorPageT = useConfiguratorPageT();

  // One write targets every product behind this line — the only difference from configure-product
  const { rows, handleChange } = useAttributeGridForm(line.attributes, ({ attributeId, value, shouldDebounce, onError }) => {
    if (line.productIds.length === 0) return;
    updateAttributeRequest({ attributeId, value, systemProductConfigurationItemIds: line.productIds }, shouldDebounce, onError);
  });

  const disabledRows = rows.filter(formItem => !!formItem.formState.disabled.value);
  const enabledRows = rows.filter(formItem => !formItem.formState.disabled.value);

  // Only reached after loading, so empty means the type genuinely owns nothing
  if (rows.length === 0) {
    return (
      <Typography px={2.4} py={2} variant="body2" color={theme.palette.customColors.darkGrey}>
        {configuratorPageT.thisProductTypeHasNoConfigurations}
      </Typography>
    );
  }

  return (
    <Stack gap={2} flex={1} overflow="hidden">
      <Stack px={2.4} pt={2}>
        <Typography variant="body2" color={theme.palette.customColors.darkGrey}>
          {line.productTypeName}
        </Typography>
        <Typography variant="caption" color={theme.palette.customColors.darkGrey}>
          {configuratorPageT.editingProductsOfType(line.productIds.length.toString())}
        </Typography>
      </Stack>
      <Stack gap={2} flex={1} overflow="auto" px={2.4} pb={2.4}>
        {disabledRows.map(formItem => (
          <AttributeRenderer key={formItem.formState.attributeId.value} formItem={formItem} handleChange={handleChange} />
        ))}
        {disabledRows.length > 0 && enabledRows.length > 0 && <Divider />}
        {enabledRows.map(formItem => (
          <AttributeRenderer key={formItem.formState.attributeId.value} formItem={formItem} handleChange={handleChange} />
        ))}
      </Stack>
    </Stack>
  );
};

export const BulkAttributeSection = connect(null, ownActions)(BulkAttributeSectionBase);
